import { DEFAULT_ROUTE_ID, routeById, type Leg, type Route } from '../content/routes';

/**
 * Trips. The trotro drives a route out and back, forever: trip 1 goes from → to, trip 2 comes back
 * on the other carriageway, and so on. `s` (distance driven, always increasing) maps to a trip, a
 * direction and `r`, the position along the route (0 at `from`, length at `to`).
 */
export type Direction = 'out' | 'back';

export interface TripPosition {
  /** 0-based trip index. */
  trip: number;
  dir: Direction;
  /** Metres along the route from its start. */
  r: number;
}

let active: Route = routeById(DEFAULT_ROUTE_ID);

export function setActiveRoute(route: Route): void {
  active = route;
}

export function activeRoute(): Route {
  return active;
}

export function directionOf(trip: number): Direction {
  return trip % 2 === 0 ? 'out' : 'back';
}

export function tripAt(s: number): TripPosition {
  const length = active.length;
  const trip = Math.max(0, Math.floor(s / length));
  const local = s - trip * length;
  const dir = directionOf(trip);
  return { trip, dir, r: dir === 'out' ? local : length - local };
}

/** World distance of route position r on a given trip. */
export function worldS(trip: number, r: number): number {
  return trip * active.length + (directionOf(trip) === 'out' ? r : active.length - r);
}

export function legIndexAt(s: number): number {
  const { r } = tripAt(s);
  const index = active.legs.findIndex((leg) => r >= leg.start && r < leg.end);
  return index === -1 ? active.legs.length - 1 : index;
}

export function legAt(s: number): Leg {
  const leg = active.legs[legIndexAt(s)];
  if (!leg) throw new Error(`route ${active.id} has no legs`);
  return leg;
}

/** Progress through the current trip, 0..1, in driving order. */
export function tripProgress(s: number): number {
  const length = active.length;
  return (s - Math.floor(s / length) * length) / length;
}

/** Where the current trip starts and ends. */
export function tripEnds(trip: number): { from: string; to: string } {
  return directionOf(trip) === 'out' ? { from: active.from, to: active.to } : { from: active.to, to: active.from };
}
